import { commerceOfferProperties } from "@/lib/analytics-ga4";
import { trackEvent, trackOwnedAnalyticsOnly } from "@/lib/analytics-events";

// ─── Impression fire-once dedupe (rule 47; 2026-07-01 chat-reading-sticky re-fire) ───
//
// A component-level fire-once ref cannot survive a remount: the sticky peek
// conditionally MOUNTED its OfferImpression island on every reveal, so each
// hide→show cycle reset the per-instance ref and re-emitted offer_seen
// (impressions/client 1.79→2.75/day; consecutive 5–60s same-client duplicates
// ~4×). The component fix keeps the island mounted, but the DEFENSIVE layer
// lives here in the shared emitter so EVERY placement is protected by
// construction: per (session × placement × plan_id), an impression is emitted
// at most once per window.
//
// Window = 60s. The rule-47 drill (2026-06-20) showed ~98% of legitimate
// duplicate impressions are ≥1 min apart (genuine re-views / navigation), while
// a <60s re-show of the same plan on the same surface is render/remount churn,
// never a new decision moment. The window is FIXED from the last EMITTED
// impression (never slid by suppressed calls), so a permanently-churning
// surface still shows ≤1 impression per window instead of going dark.
//
// Session scope = this JS module's lifetime (one page load / SPA session): a
// full reload is a genuinely new presentation and starts clean. Suppression
// stays OBSERVABLE (permanent-fix protocol #2): the first suppressed duplicate
// per key emits `agent_revenue_offer_refire_suppressed`, so a remounting
// surface still surfaces in events_raw while its offer_seen stream stays clean.
const OFFER_SHOWN_DEDUPE_WINDOW_MS = 60_000;
const lastOfferShownAtMs = new Map<string, number>();
const refireSuppressionReported = new Set<string>();

/** Test hook — clears the per-session fire-once state between test cases. */
export function __resetOfferShownDedupeForTests(): void {
  lastOfferShownAtMs.clear();
  refireSuppressionReported.clear();
}

/**
 * True when this (placement × plan_id) already emitted an impression inside the
 * dedupe window. Records the emit time when it decides NOT to suppress. Offers
 * missing either identity field are never deduped — the presentation can't be
 * keyed, and the incident class (component remount) always carries both.
 */
function shouldSuppressOfferShown(props: {
  placement?: string;
  plan_id?: string;
  source_path?: string;
}): boolean {
  if (!props.placement || !props.plan_id) return false;
  const key = `${props.placement}|${props.plan_id}`;
  const now = Date.now();
  const lastEmitted = lastOfferShownAtMs.get(key);
  if (lastEmitted !== undefined && now - lastEmitted < OFFER_SHOWN_DEDUPE_WINDOW_MS) {
    if (!refireSuppressionReported.has(key)) {
      refireSuppressionReported.add(key);
      // Bounded to once per key per session so the alarm signal can never
      // itself become a flood (rule 47 #5).
      trackEvent("agent_revenue_offer_refire_suppressed", {
        placement: props.placement,
        plan_id: props.plan_id,
        source_path: props.source_path,
        window_ms: OFFER_SHOWN_DEDUPE_WINDOW_MS,
      });
    }
    return true;
  }
  lastOfferShownAtMs.set(key, now);
  return false;
}

export function trackAgentRevenueOfferShown(
  props: {
    source_path?: string;
    landing_path?: string;
    intent?: string;
    offer_type: string;
    plan_id?: string;
    page_category?: string;
    placement?: string;
    checkout_channel?: string;
    commerce_system?: string;
    route_reason?: string;
    route_rank_score?: number;
    route_rank_reason?: string;
    ranked_offer_order?: string[];
    active_specialist?: string;
    specialist_reason?: string;
    agent_trace_id?: string;
    memory_signal?: string;
    customer_problem?: string;
    journey_action_id?: string;
    concern_id?: string;
    landing_intent?: string;
    detected_intent?: string;
    intent_confidence?: string;
    is_primary_cta?: boolean;
    cta_rank?: number;
    paywall_variant?: string;
    // chat_report_offer_v1 A/B arm (control | variant=pain-first copy). Was NOT
    // emitted, so the running pain-first A/B was unmeasurable — clicks join back
    // to this impression by readingId (source_path=/chat/{readingId}/), and the
    // arm is keyed on readingId, so emitting it here makes the whole A/B readable.
    offer_ab?: string;
    urgency?: string;
    confidence?: number;
    evidence?: string[];
    // Engine-detected personalization signals — were NULL on every offer impression
    // (2026-06-15 audit) so the per-dosha / per-band conversion of the recommendation
    // engine could not be attributed. Stamp them so the 1.81x engine-vs-shadow lift
    // is measurable by dosha + intent band. Already in the GA4 allowlist (analytics-ga4).
    strongest_dosha_id?: string;
    strongest_dosha_severity?: number;
    purchase_intent_band?: string;
  },
  opts?: {
    /**
     * Opt out of the shared 60s per-(placement × plan_id) fire-once window for a
     * surface that INTENTIONALLY re-fires impressions (none today). Prefer fixing
     * the surface; this exists so an intentional re-fire never has to bypass the
     * shared emitter (which would fork the offer_seen contract).
     */
    allowRefire?: boolean;
  }
) {
  if (!opts?.allowRefire && shouldSuppressOfferShown(props)) return;
  trackEvent("agent_revenue_offer_shown", props);
  trackEvent("commerce_offer_shown", commerceOfferProperties(props));
}

export function trackAgentDecisionMade(props: {
  agent_trace_id: string;
  session_id?: string;
  source_path?: string;
  intent?: string;
  customer_problem?: string;
  journey_action_id?: string;
  concern_id?: string;
  active_specialist?: string;
  specialist_reason?: string;
  route_reason?: string;
  memory_signal?: string;
  offer_type?: string;
  plan_id?: string;
  checkout_channel?: string;
  commerce_system?: string;
  route_rank_score?: number;
  route_rank_reason?: string;
  ranked_offer_order?: string[];
  tool_calls?: string[];
}) {
  trackEvent("agent_decision_made", props);
}

export function trackAgentRevenueOfferClicked(props: {
  source_path?: string;
  landing_path?: string;
  intent?: string;
  offer_type: string;
  plan_id?: string;
  page_category?: string;
  placement?: string;
  checkout_channel?: string;
  commerce_system?: string;
  route_reason?: string;
  route_rank_score?: number;
  route_rank_reason?: string;
  ranked_offer_order?: string[];
  agent_trace_id?: string;
  customer_problem?: string;
  journey_action_id?: string;
  concern_id?: string;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  paywall_variant?: string;
  strongest_dosha_id?: string;
  strongest_dosha_severity?: number;
  purchase_intent_band?: string;
}) {
  trackEvent("agent_revenue_offer_clicked", props);
  trackEvent("commerce_offer_clicked", commerceOfferProperties(props));
}

/**
 * The chat sticky twin only scrolls back to the real CTA. That is useful
 * re-entry/navigation telemetry, but it is NOT a monetization click and must
 * never enter agent_revenue_offer_clicked / commerce_offer_clicked or CTR.
 */
export function trackChatOfferReentryClicked(props: {
  source_path: string;
  plan_id: string;
  placement: "chat-reading-sticky";
  landing_path?: string;
  landing_intent?: string;
  route_rank_reason?: string;
}) {
  trackOwnedAnalyticsOnly("chat_offer_reentry_clicked", props);
}

export function trackChatOfferReentryShown(props: {
  source_path: string;
  plan_id: string;
  placement: "chat-reading-sticky";
  landing_path?: string;
  landing_intent?: string;
  route_rank_reason?: string;
}) {
  trackOwnedAnalyticsOnly("chat_offer_reentry_shown", props);
}

export function trackAgentRevenueOfferDismissed(props: {
  source_path?: string;
  intent?: string;
  offer_type: string;
  plan_id?: string;
  page_category?: string;
  placement?: string;
  checkout_channel?: string;
  commerce_system?: string;
  route_reason?: string;
  customer_problem?: string;
  urgency?: string;
  confidence?: number;
}) {
  trackEvent("agent_revenue_offer_dismissed", props);
}

export function trackShopifyProductClicked(props: {
  source_path?: string;
  intent?: string;
  page_category?: string;
  product_sku: string;
  product_name: string;
  triggered_by_dosha?: string | null;
  price_range?: string;
  placement: string;
  checkout_channel: "shopify_product";
  commerce_system: "shopify";
  customer_problem?: string;
  urgency?: string;
  confidence?: number;
}) {
  trackEvent("shopify_product_clicked", props);
  trackEvent(
    "commerce_offer_clicked",
    commerceOfferProperties({ ...props, offer_type: "product" })
  );
}

export function trackShopifyCartStarted(props: {
  source_path?: string;
  intent?: string;
  page_category?: string;
  product_skus: string[];
  product_count: number;
  placement: string;
  checkout_channel: "shopify_product";
  commerce_system: "shopify";
  customer_problem?: string;
  urgency?: string;
  confidence?: number;
}) {
  trackEvent("shopify_cart_started", props);
  trackEvent(
    "commerce_add_to_cart",
    commerceOfferProperties({ ...props, offer_type: "product_cart" })
  );
}
